/** Never pass an empty/expired secret to Connect's automatic claim loop. */
export async function requestStripeSession({ getToken, invalidateToken, signal, expectedStripeAccountId, request, timeoutMs = 10000 }) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) abort();
  const timeout = setTimeout(abort, timeoutMs);
  const aborted = new Promise((_, reject) => {
    const rejectAbort = () => reject(new DOMException('Stripe session request timed out or was cancelled', 'AbortError'));
    if (controller.signal.aborted) rejectAbort();
    else controller.signal.addEventListener('abort', rejectAbort, { once: true });
  });
  const run = async () => {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      if (controller.signal.aborted) throw new DOMException('Cancelled', 'AbortError');
      if (attempt) await invalidateToken?.();
      const token = await getToken();
      if (controller.signal.aborted) throw new DOMException('Cancelled', 'AbortError');
      const response = await request({ authorization: token, expectedStripeAccountId, signal: controller.signal });
      if (!response.ok) {
        if (attempt === 0 && (response.status === 401 || response.status >= 500)) continue;
        const error = new Error(response.status === 403 ? 'An organization admin must manage Stripe setup.' : response.status === 409 ? 'Your Stripe account needs reconciliation. Contact support.' : 'We could not start a secure Stripe session. Please try again.');
        error.status = response.status;
        throw error;
      }
      const data = await response.json();
      if (typeof data.client_secret !== 'string' || !data.client_secret.trim()) throw new Error('Stripe returned an incomplete session. Please try again.');
      if (controller.signal.aborted) throw new DOMException('Cancelled', 'AbortError');
      return data.client_secret;
    }
  };
  try { return await Promise.race([run(), aborted]); }
  finally { clearTimeout(timeout); signal?.removeEventListener('abort', abort); }
}
